import { CalendarDays, CircleDollarSign, Image as ImageIcon, Mail, MapPin, Pencil, Phone, UserRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PHASE_LABELS } from "@/utils/deals.utils";
import { absoluteUrl, cn, formatUtcDate, formatCurrency } from "@/utils/common.utils";
import UserAvatar from "@/components/common/UserAvatar";
import ImageDrop from "@/components/deals/ImageDrop";
import StatusBadge from "@/components/common/StatusBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DealType } from "@/enum/common.enum";
import type { Deal } from "@/types/deals.types";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

interface DealIdentityHeaderProps {
  deal: Deal;
  /** Server-computed deal health is anything but on_track. Same source as the
   *  Deal Status banner, so the two can never disagree. */
  needsAttention?: boolean;
  children?: ReactNode;
  /** Opens the address dialog. Omitted for read-only viewers. */
  onEditAddress?: () => void;
  /** Saves a new cover photo URL ("" removes it); rejects on failure. Omitted for read-only viewers. */
  onChangeCover?: (url: string) => Promise<unknown>;
}

export default function DealIdentityHeader({ deal, needsAttention = false, children, onEditAddress, onChangeCover }: DealIdentityHeaderProps) {
  const [coverOpen, setCoverOpen] = useState(false);
  // Rejections propagate so ImageDrop keeps the modal open on failure.
  const saveCover = async (url: string) => {
    const hadPhoto = Boolean(deal.hero_image_url);
    await onChangeCover?.(url);
    toast.success(!url ? "Cover photo removed" : hadPhoto ? "Cover photo replaced" : "Cover photo added");
    setCoverOpen(false);
  };
  const closingDate = deal.contract_terms?.closing_date;
  const placeholder = Boolean((deal as Record<string, any>).address_is_placeholder);
  const coRole = deal.deal_type === DealType.SELL ? "Co-seller" : "Co-buyer";
  const people = [
    ...(deal.client_name ? [{ role: "Client", name: deal.client_name, email: deal.client_email, phone: deal.client_phone }] : []),
    ...(deal.additional_clients || []).filter((c) => c.name).map((c) => ({ role: coRole, ...c })),
  ];
  const meta = [
    deal.agent_name && { k: "Agent", v: deal.agent_name, Icon: UserRound },
    closingDate && { k: "Closing", v: formatUtcDate(closingDate, { year: "numeric" }), Icon: CalendarDays },
    deal.list_price && { k: "Value", v: formatCurrency(deal.list_price), Icon: CircleDollarSign },
  ].filter(Boolean) as { k: string; v: ReactNode; Icon: LucideIcon }[];

  return (
    <div className="mb-2">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className={`min-w-0 basis-full break-words font-display text-2xl tracking-tight sm:basis-auto sm:text-3xl${placeholder ? " text-stone italic" : ""}`}>{deal.address}</h1>
        {onEditAddress && (
          placeholder ? (
            // Loud while there is no property: this is the one field a
            // house-hunting deal is actively waiting on.
            <button
              type="button"
              onClick={onEditAddress}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dash-border px-2.5 py-1 text-xs font-medium text-dash-ink transition-colors hover:bg-bone"
              data-testid="set-deal-address"
            >
              <MapPin className="size-3.5" /> Add property address
            </button>
          ) : (
            <button
              type="button"
              onClick={onEditAddress}
              className="inline-flex items-center gap-1 rounded-lg p-1 text-stone transition-colors hover:bg-bone hover:text-ink"
              aria-label="Edit property address"
              data-testid="edit-deal-address"
            >
              <Pencil className="size-3.5" />
            </button>
          )
        )}
        <StatusBadge tone="success" className="px-2.5 py-1 text-xs font-medium">{deal.deal_type === DealType.SELL ? "Seller side" : "Buyer rep"}</StatusBadge>
        <StatusBadge tone="info" className="px-2.5 py-1 text-xs font-medium">{PHASE_LABELS[deal.phase]}</StatusBadge>
        {needsAttention && <StatusBadge tone="warning" className="px-2.5 py-1 text-xs font-medium" data-testid="deal-attention-chip">Attention</StatusBadge>}
        {/* The listing photo lives in a modal, so it never pushes the deal
            down the page. No photo and read-only: nothing to show. */}
        {(deal.hero_image_url || onChangeCover) && (
          <Dialog open={coverOpen} onOpenChange={setCoverOpen}>
            <DialogTrigger
              className="inline-flex items-center gap-1.5 rounded-lg border border-dash-border px-2.5 py-1 text-xs font-medium text-dash-ink transition-colors hover:bg-bone"
              data-testid="deal-cover-open"
            >
              <ImageIcon className="size-3.5" /> {deal.hero_image_url ? "View cover photo" : "Add cover photo"}
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Cover photo</DialogTitle>
                <DialogDescription>
                  {onChangeCover
                    ? "Shows on the board, the client portal and the shared page. Use Replace photo to upload a new one."
                    : "Shows on the board, the client portal and the shared page."}
                </DialogDescription>
              </DialogHeader>
              {onChangeCover ? (
                <ImageDrop
                  value={deal.hero_image_url}
                  onChange={saveCover}
                  label="Add a cover photo"
                  helperText="Drag & drop or click · JPG, PNG or WEBP up to 8MB"
                  className="overflow-hidden rounded-xl"
                  testId="deal-cover"
                />
              ) : (
                <img src={absoluteUrl(deal.hero_image_url)} alt={`Photo of ${deal.address}`} className="aspect-video w-full rounded-xl object-cover" />
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {meta.length > 0 && (
        <dl className="mb-5 flex flex-wrap gap-x-8 gap-y-3">
          {meta.map(({ k, v, Icon }) => (
            <div key={k} className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-dash-hover text-dash-greenDeep" aria-hidden>
                <Icon className="size-4" />
              </span>
              <div className="min-w-0">
                <dt className="text-xs text-dash-muted">{k}</dt>
                <dd className="text-sm font-semibold text-dash-ink">{v}</dd>
              </div>
            </div>
          ))}
        </dl>
      )}

      {people.length > 0 && (
        <section className="mb-5" aria-label={people.length > 1 ? "Clients" : "Client"}>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-dash-muted">
            {people.length > 1 ? `Clients · ${people.length}` : "Client"}
          </h2>
          <ul className="flex flex-wrap gap-2" data-testid="deal-people">
            {people.map((person, i) => {
              const primary = i === 0 && person.role === "Client";
              const email = person.email?.toLowerCase();
              const photo = email ? deal.participants?.find((p) => p.email?.toLowerCase() === email)?.photo_url : undefined;
              const avatar = (size: string) => (
                <UserAvatar
                  name={person.name}
                  src={photo}
                  aria-hidden
                  className={cn("shrink-0", size)}
                  fallbackClassName={cn("font-semibold", size,
                    primary ? "bg-dash-greenDeep text-white" : "bg-dash-hover text-dash-greenDeep")}
                />
              );
              return (
                <li key={i} className="min-w-0">
                  <Popover>
                    <PopoverTrigger className="flex max-w-full items-center gap-2 rounded-full border border-dash-border bg-white py-1 pl-1 pr-3 transition-colors hover:border-dash-greenDeep/40 data-[state=open]:border-dash-greenDeep/40">
                      {avatar("size-6 text-[10px]")}
                      <span className="truncate text-sm font-medium text-dash-ink">{person.name}</span>
                      {!primary && <span className="shrink-0 text-[11px] text-dash-muted">{person.role}</span>}
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto min-w-64 max-w-[calc(100vw-2rem)] rounded-xl border-dash-border p-3">
                      <div className="flex min-w-0 items-center gap-3">
                        {avatar("size-9 text-xs")}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-dash-ink">{person.name}</p>
                          <p className="text-xs text-dash-muted">{person.role}</p>
                        </div>
                      </div>
                      {(person.email || person.phone) && (
                        <div className="mt-3 space-y-1.5 border-t border-dash-borderSoft pt-3">
                          {person.email && (
                            <a href={`mailto:${person.email}`} className="flex min-w-0 items-center gap-2 text-sm text-dash-muted hover:text-dash-ink" title={person.email}>
                              <Mail className="size-3.5 shrink-0" aria-hidden /><span className="truncate">{person.email}</span>
                            </a>
                          )}
                          {person.phone && (
                            <a href={`tel:${person.phone}`} className="flex items-center gap-2 whitespace-nowrap text-sm text-dash-muted hover:text-dash-ink">
                              <Phone className="size-3.5 shrink-0" aria-hidden />{person.phone}
                            </a>
                          )}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {children}
    </div>
  );
}
